import { useMemo } from 'react';

import type { FeatureToggleDto } from '@/services/ws-feature-toggle/src/main/resources/rest-api';
import { getFeatureToggles, FeatureToggleName } from '@/services/ws-feature-toggle/src/main/resources/rest-api';
import { createContexts } from '@/js/libs/create-contexts';
import { useSubscribe } from '@/js/libs/observable';
import { useIdentityUser } from '@/js/libs/use-auth';
import { NotActive, useRequest } from '@/js/libs/use-request';

type FeaturesMap = {
  [value in FeatureToggleName]?: boolean;
};

const { useFeatureToggleContext } = createContexts({
  FeatureToggle() {
    const user = useIdentityUser();

    const featureTogglesRequest = useRequest({
      apiMethod: getFeatureToggles,
      params: user ? undefined : NotActive,
      queryDefaults: false,
    });

    const featureToggles = useSubscribe(featureTogglesRequest.data$, (response) => response?.data ?? []);
    const pending = useSubscribe(featureTogglesRequest.pending$);
    const { retry } = featureTogglesRequest;

    const featuresMap = useMemo(
      () =>
        featureToggles.reduce<FeaturesMap>((acc, feature) => {
          if (!feature.name || !feature.enabled) {
            return acc;
          }

          return {
            ...acc,
            [feature.name]: feature.enabled,
          };
        }, {}),
      [featureToggles],
    );

    const isOnlinePositionsRenameEnabled = featuresMap[FeatureToggleName.ONLINE_POSITIONS_RENAME];
    const isMailingPositionsRenameEnabled = featuresMap[FeatureToggleName.MAILING_POSITIONS_RENAME];
    const isInternalAccountToggleEnabled = featuresMap[FeatureToggleName.INTERNAL_ACCOUNT];
    const isFlapPromopostProductEnabled = featuresMap[FeatureToggleName.FLAP_PROMOPOST_PRODUCT];
    const isRevisedPromopostDeliveryDaysEnabled = featuresMap[FeatureToggleName.REVISED_PROMOPOST_DELIVERY_DAYS];
    const isPostAppOnlineInventoryEnabled = featuresMap[FeatureToggleName.POST_APP_ONLINE_INVENTORY];
    const isAdditionalPromopostProductsEnabled = featuresMap[FeatureToggleName.ADDITIONAL_PROMOPOST_PRODUCTS];
    const isInMailChannelEnabled = featuresMap[FeatureToggleName.INMAIL_CHANNEL];

    return {
      pending,
      featureToggles: featureToggles as FeatureToggleDto[],
      retry,
      isOnlinePositionsRenameEnabled,
      isMailingPositionsRenameEnabled,
      isInternalAccountToggleEnabled,
      isFlapPromopostProductEnabled,
      isRevisedPromopostDeliveryDaysEnabled,
      isPostAppOnlineInventoryEnabled,
      isAdditionalPromopostProductsEnabled,
      isInMailChannelEnabled,
    };
  },
});

export { useFeatureToggleContext };
